'use client'

import { useState, useRef, useCallback } from 'react'

// ---------------------------------------------------------------------------
// AudioWorklet processor source (inlined as a blob URL)
// Captures raw PCM float32 samples from the mic and posts them to the main thread.
// ---------------------------------------------------------------------------
const WORKLET_PROCESSOR_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input[0]) {
      // Send a copy of the float32 samples to the main thread
      this.port.postMessage(new Float32Array(input[0]));
    }
    return true;
  }
}
registerProcessor('pcm-capture-processor', PcmCaptureProcessor);
`

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface UseStreamingVoiceRecorderOptions {
  onPartialTranscript: (text: string) => void
  onFinalTranscript: (text: string) => void
  onError: (error: string) => void
}

interface UseStreamingVoiceRecorderReturn {
  startRecording: () => Promise<void>
  stopRecording: () => void
  isRecording: boolean
  isConnecting: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert Float32 audio samples to 16-bit PCM and return as base64 */
function float32ToPcm16Base64(float32: Float32Array): string {
  const pcm16 = new Int16Array(float32.length)
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]))
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  const bytes = new Uint8Array(pcm16.buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/** Downsample from source sample rate to target (24kHz) */
function downsample(
  buffer: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number,
): Float32Array {
  if (sourceSampleRate === targetSampleRate) return buffer
  const ratio = sourceSampleRate / targetSampleRate
  const newLength = Math.round(buffer.length / ratio)
  const result = new Float32Array(newLength)
  for (let i = 0; i < newLength; i++) {
    const index = Math.round(i * ratio)
    result[i] = buffer[Math.min(index, buffer.length - 1)]
  }
  return result
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useStreamingVoiceRecorder(
  options: UseStreamingVoiceRecorderOptions,
): UseStreamingVoiceRecorderReturn {
  const { onPartialTranscript, onFinalTranscript, onError } = options

  const [isRecording, setIsRecording] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)

  // Stable refs for callbacks so we don't need them in useCallback deps
  const onPartialRef = useRef(onPartialTranscript)
  onPartialRef.current = onPartialTranscript
  const onFinalRef = useRef(onFinalTranscript)
  onFinalRef.current = onFinalTranscript
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const cleanup = useCallback(() => {
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onmessage = null
      wsRef.current.onerror = null
      wsRef.current.onclose = null
      if (wsRef.current.readyState < 2) {
        wsRef.current.close()
      }
      wsRef.current = null
    }

    // Disconnect worklet
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect()
      workletNodeRef.current = null
    }

    // Close AudioContext
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    // Stop mic tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    setIsRecording(false)
    setIsConnecting(false)
  }, [])

  const startAudioPipeline = useCallback(
    (stream: MediaStream, ws: WebSocket) => {
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      const source = audioContext.createMediaStreamSource(stream)

      // Create worklet blob URL
      const blob = new Blob([WORKLET_PROCESSOR_SOURCE], {
        type: 'application/javascript',
      })
      const workletUrl = URL.createObjectURL(blob)

      audioContext.audioWorklet
        .addModule(workletUrl)
        .catch(() => {
          cleanup()
          onErrorRef.current('Audio pipeline failed')
        })
        .then(() => {
          URL.revokeObjectURL(workletUrl)

          const workletNode = new AudioWorkletNode(
            audioContext,
            'pcm-capture-processor',
          )
          workletNodeRef.current = workletNode

          workletNode.port.onmessage = (event: MessageEvent) => {
            if (ws.readyState !== 1) return // Only send if WS is OPEN

            const float32Data = event.data as Float32Array
            const downsampled = downsample(
              float32Data,
              audioContext.sampleRate,
              24000,
            )
            const base64Audio = float32ToPcm16Base64(downsampled)

            ws.send(
              JSON.stringify({
                type: 'input_audio_buffer.append',
                audio: base64Audio,
              }),
            )
          }

          source.connect(workletNode)
          workletNode.connect(audioContext.destination)
        })
    },
    [cleanup],
  )

  const startRecording = useCallback(async () => {
    if (wsRef.current) return

    setIsConnecting(true)

    try {
      // 1. Get ephemeral token from server
      const tokenResponse = await fetch('/api/realtime-token', {
        method: 'POST',
      })
      if (!tokenResponse.ok) {
        throw new Error('Failed to get realtime token')
      }
      const { token } = await tokenResponse.json()

      // 2. Get microphone access
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (err) {
        cleanup()
        if (
          err instanceof Error &&
          (err.name === 'NotAllowedError' ||
            err.name === 'PermissionDeniedError')
        ) {
          onErrorRef.current('Microphone permission denied')
        } else {
          onErrorRef.current('Failed to access microphone')
        }
        return
      }
      streamRef.current = stream

      // 3. Connect to OpenAI Realtime API via WebSocket
      const ws = new WebSocket(
        'wss://api.openai.com/v1/realtime?intent=transcription',
        ['realtime', `openai-insecure-api-key.${token}`],
      )
      wsRef.current = ws

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: 'transcription_session.update',
            session: {
              input_audio_format: 'pcm16',
              input_audio_transcription: {
                model: 'gpt-4o-mini-transcribe',
                language: 'en',
              },
              turn_detection: {
                type: 'server_vad',
                threshold: 0.3,
                prefix_padding_ms: 100,
                silence_duration_ms: 500,
              },
              input_audio_noise_reduction: null,
            },
          }),
        )

        startAudioPipeline(stream, ws)
        setIsConnecting(false)
        setIsRecording(true)
      }

      ws.onmessage = (event: MessageEvent) => {
        let data: Record<string, string>
        try {
          data = JSON.parse(event.data as string)
        } catch {
          return
        }

        if (data.type === 'conversation.item.input_audio_transcription.delta') {
          onPartialRef.current(data.delta)
        } else if (
          data.type === 'conversation.item.input_audio_transcription.completed'
        ) {
          onFinalRef.current(data.transcript)
        }
      }

      ws.onerror = () => {
        cleanup()
        onErrorRef.current('WebSocket connection failed')
      }

      ws.onclose = () => {
        cleanup()
      }
    } catch (err) {
      cleanup()
      onErrorRef.current(
        err instanceof Error ? err.message : 'Failed to start recording',
      )
    }
  }, [cleanup, startAudioPipeline])

  const stopRecording = useCallback(() => {
    cleanup()
  }, [cleanup])

  return {
    startRecording,
    stopRecording,
    isRecording,
    isConnecting,
  }
}
